import type { BetSelection } from "@/features/betslip/types";
import {
  isGranted,
  type PermissionReason,
  type Permissions,
} from "@/features/auth/models/permissions";

export type BettingPermissions = {
  submitBet: boolean;
  submitRacingBet: boolean;
  submitEsportsBet: boolean;
  submitSportsBet: boolean;
  submitRacingExoticBet: boolean;
  submitPlaceBet: boolean;
  submitTop4Bet: boolean;
  submitSRMBet: boolean;
  submitQuaddieBet: boolean;
};

export type BetslipPermissions = {
  internationalRacingAllowed: boolean;
  top4Allowed: boolean;
  placeAllowed: boolean;
  srmAllowed: boolean;
  quaddieAllowed: boolean;
  exoticAllowed: boolean;
};

const RACING_MARKET_PERMISSION: Record<
  string,
  keyof BettingPermissions | undefined
> = {
  EXACTA: "submitRacingExoticBet",
  FIRST_4: "submitRacingExoticBet",
  QUINELLA: "submitRacingExoticBet",
  TRIFECTA: "submitRacingExoticBet",

  RACING_PLACE: "submitPlaceBet",
  RACING_PLACE_SP: "submitPlaceBet",
  RACING_WINNER: "submitPlaceBet",
  RACING_WINNER_SP: "submitPlaceBet",

  RACING_TOP_2: "submitTop4Bet",
  RACING_TOP_2_SP: "submitTop4Bet",
  RACING_TOP_3: "submitTop4Bet",
  RACING_TOP_3_SP: "submitTop4Bet",
  RACING_TOP_4: "submitTop4Bet",
  RACING_TOP_4_SP: "submitTop4Bet",
};

const HUB_PERMISSION: Record<string, keyof BettingPermissions> = {
  racing: "submitRacingBet",
  esports: "submitEsportsBet",
  sports: "submitSportsBet",
};

const TOP_MARKET_NAMES = new Set(["Top 2", "Top 3", "Top 4"]);
const PLACE_MARKET_NAME = "Place";

const allowedUnlessBlocked = (reason: PermissionReason | undefined): boolean =>
  reason !== "BLOCKED";

const allowedOnlyIfGranted = (reason: PermissionReason | undefined): boolean =>
  reason === undefined || reason === "GRANTED";

/**
 * Collapse the session's permission reasons into the boolean map
 * `isPermittedToBet` consumes. Everything here requires an explicit
 * GRANTED — an absent or blocked reason reads as not permitted.
 */
export const toBettingPermissions = (
  perms: Permissions,
): BettingPermissions => ({
  submitBet: isGranted(perms.submitBet),
  submitRacingBet: isGranted(perms.submitRacingBet),
  submitEsportsBet: isGranted(perms.submitEsportsBet),
  submitSportsBet: isGranted(perms.submitSportsBet),
  submitRacingExoticBet: isGranted(perms.submitRacingExoticBet),
  submitPlaceBet: isGranted(perms.submitPlaceBet),
  submitTop4Bet: isGranted(perms.submitTop4Bet),
  submitSRMBet: isGranted(perms.submitSRMBet),
  submitQuaddieBet: isGranted(perms.submitQuaddieBet),
});

export const summariseBetPermissions = (
  perms: Permissions,
): BetslipPermissions => ({
  internationalRacingAllowed: allowedUnlessBlocked(
    perms.submitInternationalRacingBet,
  ),
  top4Allowed: allowedOnlyIfGranted(perms.submitTop4Bet),
  placeAllowed: allowedOnlyIfGranted(perms.submitPlaceBet),
  srmAllowed: allowedOnlyIfGranted(perms.submitSRMBet),
  quaddieAllowed: allowedOnlyIfGranted(perms.submitQuaddieBet),
  exoticAllowed: allowedOnlyIfGranted(perms.submitRacingExoticBet),
});

export const isPermittedToBet = (
  permissions: BettingPermissions,
  selection: BetSelection,
  userEventFilters?: string[],
): boolean => {
  if (!permissions.submitBet) return false;

  const hubPermission = HUB_PERMISSION[selection.hub];
  if (hubPermission && !permissions[hubPermission]) return false;

  if (selection.hub === "racing") {
    if (!hasRacingPermission(permissions, selection)) return false;
  }

  if (userEventFilters?.length) {
    const selectionFilters = new Set(selection.filters);
    if (!userEventFilters.every((filter) => selectionFilters.has(filter))) {
      return false;
    }
  }

  return true;
};

/**
 * Returns true when a selection already in the slip is no longer
 * permitted under a fresh bet-kind snapshot. Used by the prune flow on
 * permission changes. Pure: no store reads.
 */
export const isSelectionDisallowed = (
  selection: BetSelection,
  flags: BetslipPermissions,
): boolean => {
  if (selection.kind === "quaddie" && !flags.quaddieAllowed) {
    return true;
  }

  if (selection.kind === "exotic" && !flags.exoticAllowed) {
    return true;
  }

  if (
    selection.kind === "sem" &&
    selection.hub === "racing" &&
    !flags.srmAllowed
  ) {
    return true;
  }

  if (
    selection.kind === "race" &&
    TOP_MARKET_NAMES.has(selection.marketName ?? "") &&
    !flags.top4Allowed
  ) {
    return true;
  }

  if (
    selection.kind === "race" &&
    selection.marketName === PLACE_MARKET_NAME &&
    !flags.placeAllowed
  ) {
    return true;
  }

  if (
    (selection.kind === "race" ||
      (selection.kind === "sem" && selection.hub === "racing")) &&
    selection.region === "ROW" &&
    !flags.internationalRacingAllowed
  ) {
    return true;
  }

  return false;
};

const hasRacingPermission = (
  permissions: BettingPermissions,
  selection: BetSelection,
): boolean => {
  if (selection.kind === "sem") {
    return permissions.submitSRMBet;
  }

  if (selection.kind === "quaddie") {
    return permissions.submitQuaddieBet;
  }

  if (selection.kind === "exotic") {
    return permissions.submitRacingExoticBet;
  }

  const { marketType } = selection;
  if (marketType && marketType !== "CUSTOM") {
    const permissionKey = RACING_MARKET_PERMISSION[marketType];
    if (permissionKey) return permissions[permissionKey];
    // A racing market type without a mapped permission is denied: fail
    // closed rather than let an unclassified market through.
    return false;
  }

  // CUSTOM or no market type — allow
  return true;
};
